import { useState, useEffect, useRef, useCallback } from 'react'
import { providerApiClient } from '@/lib/api'

interface UseTokenRefreshOptions {
  isAuthenticated: boolean
  onRefreshError?: (error: Error) => void
}

// TODO: Sync with backend token lifetime (auth.py ACCESS_TOKEN_EXPIRE_MINUTES)
const TOKEN_LIFETIME_MS = 30 * 60 * 1000 // 30 minutes
const REFRESH_BUFFER_MS = 5 * 60 * 1000 // 5 minutes before expiry
const VISIBILITY_THROTTLE_MS = 60 * 1000 // 1 minute throttle

export function useTokenRefresh({ isAuthenticated, onRefreshError }: UseTokenRefreshOptions) {
  const [lastRefreshAt, setLastRefreshAt] = useState<number | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastRefreshAtRef = useRef<number | null>(null)
  const lastVisibilityRefreshRef = useRef<number | null>(null)
  const isRefreshingRef = useRef(false)
  const onRefreshErrorRef = useRef(onRefreshError)
  onRefreshErrorRef.current = onRefreshError

  const refreshIntervalMs = TOKEN_LIFETIME_MS - REFRESH_BUFFER_MS

  const clearRefreshTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const doRefresh = useCallback(async (): Promise<boolean> => {
    if (isRefreshingRef.current) return false

    isRefreshingRef.current = true
    setIsRefreshing(true)

    try {
      await providerApiClient.refreshToken()
      const now = Date.now()
      setLastRefreshAt(now)
      lastRefreshAtRef.current = now
      return true
    } catch (error) {
      onRefreshErrorRef.current?.(error instanceof Error ? error : new Error('Token refresh failed'))
      return false
    } finally {
      isRefreshingRef.current = false
      setIsRefreshing(false)
    }
  }, [])

  const scheduleRefresh = useCallback(() => {
    clearRefreshTimer()
    if (!isAuthenticated) return

    timerRef.current = setTimeout(async () => {
      const success = await doRefresh()
      if (success) {
        scheduleRefresh()
      }
    }, refreshIntervalMs)
  }, [isAuthenticated, refreshIntervalMs, doRefresh, clearRefreshTimer])

  const refreshNow = useCallback(async (): Promise<void> => {
    if (isRefreshingRef.current) return
    const success = await doRefresh()
    if (success) {
      scheduleRefresh()
    }
  }, [doRefresh, scheduleRefresh])

  // Handle visibility change
  useEffect(() => {
    if (!isAuthenticated) return

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return

      const now = Date.now()
      const lastVisibilityRefresh = lastVisibilityRefreshRef.current
      if (lastVisibilityRefresh && now - lastVisibilityRefresh < VISIBILITY_THROTTLE_MS) return

      const lastRefresh = lastRefreshAtRef.current
      if (!lastRefresh || now - lastRefresh >= refreshIntervalMs) {
        lastVisibilityRefreshRef.current = now
        refreshNow()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [isAuthenticated, refreshIntervalMs, refreshNow])

  // Schedule refresh when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const now = Date.now()
      setLastRefreshAt(now)
      lastRefreshAtRef.current = now
      scheduleRefresh()
    } else {
      clearRefreshTimer()
      setLastRefreshAt(null)
      lastRefreshAtRef.current = null
    }

    return () => clearRefreshTimer()
  }, [isAuthenticated, scheduleRefresh, clearRefreshTimer])

  return { lastRefreshAt, isRefreshing, refreshNow }
}
